import LinearProgress from "@mui/material/LinearProgress";
import { createBrowserRouter, Navigate, useParams, useSearchParams } from "react-router";

import { LoginPage, RequireAuth } from "@/features/auth";

import { AppShell } from "./layout/AppShell";

/** 页面按需加载：表格页依赖的 Data Grid 只在打开时下载，首屏更快。 */
/** 首屏要先下载按需加载的页面：顶部一条进度条，而不是白屏。 */
function Loading() {
  return <LinearProgress sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999 }} />;
}

/**
 * 老地址 `/gateways?tab=deliveries` 收藏夹里还有，带着查询串转到新地址。
 * 种类段缺省是 `sub` —— 线上现在只有这一种。
 */
function GatewayRedirect() {
  const { kind = "sub" } = useParams();
  const [params] = useSearchParams();
  const tab = params.get("tab") === "deliveries" ? "deliveries" : "placements";
  const rest = new URLSearchParams(params);
  rest.delete("tab");
  const search = rest.toString();
  return <Navigate to={`/gateways/${kind}/${tab}${search ? `?${search}` : ""}`} replace />;
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  {
    path: "/",
    HydrateFallback: Loading,
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, lazy: async () => ({ Component: (await import("@/features/dashboard")).DashboardPage }) },

      // 资源：任务消耗的一切供给
      { path: "resources/servers", lazy: async () => ({ Component: (await import("@/features/fleet")).ServersPage }) },
      { path: "resources/agents", lazy: async () => ({ Component: (await import("@/features/fleet")).AgentsPage }) },
      { path: "resources/mailboxes", lazy: async () => ({ Component: (await import("@/features/inventory")).MailboxesPage }) },
      { path: "resources/sms", lazy: async () => ({ Component: (await import("@/features/providers")).SmsPage }) },
      { path: "resources/proxies/dynamic", lazy: async () => ({ Component: (await import("@/features/inventory")).DynamicProxiesPage }) },
      { path: "resources/proxies/static", lazy: async () => ({ Component: (await import("@/features/inventory")).StaticLinesPage }) },
      { path: "resources/search", lazy: async () => ({ Component: (await import("@/features/providers")).SearchPage }) },
      { path: "resources/sandbox", lazy: async () => ({ Component: (await import("@/features/providers")).SandboxPage }) },

      // 账号：产出，成功率是它的视图
      { path: "accounts", lazy: async () => ({ Component: (await import("@/features/accounts")).AccountsPage }) },
      { path: "accounts/success-rate", lazy: async () => ({ Component: (await import("@/features/insights")).SuccessRatePage }) },
      // C：只人工粘贴导入，所以它那一支的主动作是「录入账号」，列也和 G 不一样
      { path: "accounts/c", lazy: async () => ({ Component: (await import("@/features/accounts")).ClaudeAccountsPage }) },

      // 网关：地址里带种类与标签页（/gateways/sub/placements），菜单的子项就是这些地址
      { path: "gateways/:kind/:tab", lazy: async () => ({ Component: (await import("@/features/gateways")).GatewaysPage }) },
      { path: "gateways/:kind", element: <GatewayRedirect /> },

      // 任务
      { path: "tasks/standing", lazy: async () => ({ Component: (await import("@/features/standing")).StandingPage }) },
      { path: "tasks/new", lazy: async () => ({ Component: (await import("@/features/pipelines")).CreateTaskPage }) },
      { path: "tasks/pipelines", lazy: async () => ({ Component: (await import("@/features/pipelines")).PipelinesPage }) },
      { path: "tasks/runs", lazy: async () => ({ Component: (await import("@/features/tasks")).RunsPage }) },
      { path: "tasks/runs/:id", lazy: async () => ({ Component: (await import("@/features/tasks")).RunDetailPage }) },
      { path: "tasks/types", lazy: async () => ({ Component: (await import("@/features/tasks")).TaskTypesPage }) },
      { path: "tasks/logs", lazy: async () => ({ Component: (await import("@/features/tasks")).LogsPage }) },

      // 对外：给客户的 API 文档
      { path: "api-docs", lazy: async () => ({ Component: (await import("@/features/apidocs")).ApiDocsPage }) },

      { path: "costs", lazy: async () => ({ Component: (await import("@/features/costs")).CostsPage }) },
      { path: "settings", lazy: async () => ({ Component: (await import("@/features/settings")).SettingsPage }) },

      // 旧地址仍然可用：收藏夹与历史链接不报 404
      { path: "machines", element: <Navigate to="/resources/agents" replace /> },
      { path: "providers", element: <Navigate to="/resources/sms" replace /> },
      { path: "resources/proxies", element: <Navigate to="/resources/proxies/static" replace /> },
      { path: "success-rate", element: <Navigate to="/accounts/success-rate" replace /> },
      { path: "gateways", element: <GatewayRedirect /> },
      { path: "jobs", element: <Navigate to="/tasks/runs" replace /> },
      { path: "jobs/:id", element: <Navigate to="/tasks/runs" replace /> },
    ],
  },
]);
